import {
  CheckIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Collapse,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useMemo, useState } from "react";
import { CVSectionContainer } from "../../../../../components/common/CVSectionContainer";
import { DeleteConfirmationModal } from "../../../../../components/common/DeleteConfirmationModal";
import { ValidationResult } from "../../../../../components/common/ValidationResult";
import { FormUIColor } from "../../../../../constants/colorPalette";
import { useEditMode } from "../../../../../context/EditModeContext";
import { useLanguageSkillMutations } from "../../../../../hooks/mutations/useLanguageSkillMutations";
import {
  useLanguageDictionary,
  useLanguageProficiencies,
} from "../../../../../hooks/queries/useLanguageDictionary";
import { useLanguageSkills } from "../../../../../hooks/queries/useLanguageSkills";
import { Language } from "../../../../../types/cv";
import { ValidateableCvSections } from "../../../../../types/cvSections";

const INSTRUCTIONS = (
  <Stack gap={5}>
    <Text fw={500}>Instructions from your company</Text>
    <Text size="sm">
      Provide your language proficiencies to show your international
      communication abilities.
    </Text>
  </Stack>
);

interface LanguagesProps {
  userId: number;
  cvId?: number;
  /** Called after any successful mutation (add, update, delete) so the parent
   *  can react — e.g. trigger a profile completion refetch. */
  onMutated?: () => void;
}

export function Languages({ userId, cvId, onMutated }: LanguagesProps) {
  const { isEditMode } = useEditMode();
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingLanguageId, setEditingLanguageId] = useState<string | null>(
    null
  );
  const [newLanguage, setNewLanguage] = useState<{
    languageId: number | null;
    proficiencyId: number | null;
  }>({
    languageId: null,
    proficiencyId: null,
  });

  const [editLanguage, setEditLanguage] = useState<{
    languageId: number | null;
    proficiencyId: number | null;
  }>({
    languageId: null,
    proficiencyId: null,
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState<Language | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showAddValidation, setShowAddValidation] = useState(false);
  const [showEditValidation, setShowEditValidation] = useState(false);

  // React Query hooks
  const { data: languageSkills = [], isLoading: languageSkillsLoading } =
    useLanguageSkills(userId || 0);
  const { data: availableLanguages = [], isLoading: languagesLoading } =
    useLanguageDictionary();
  const { data: proficiencyLevels = [], isLoading: proficienciesLoading } =
    useLanguageProficiencies();

  // Mutations
  const { addLanguageSkill, updateLanguageSkill, deleteLanguageSkill } =
    useLanguageSkillMutations();

  // Combined loading state
  const isComponentLoading =
    languageSkillsLoading || languagesLoading || proficienciesLoading;

  // Close and reset all forms when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAddFormOpen(false);
      setEditingLanguageId(null);
      setNewLanguage({
        languageId: null,
        proficiencyId: null,
      });
      setDeleteModalOpen(false);
    }
  }, [isEditMode]);

  // Function to format languages data for LLM validation
  const getLanguagesSectionData = (): string => {
    if (isComponentLoading) {
      return 'Loading languages data...';
    }

    if (!languageSkills || languageSkills.length === 0) {
      return 'No language skills found.';
    }

    return languageSkills.map((lang, index) => {
      // Look up the language and proficiency from the dictionaries
      const languageOption = availableLanguages.find((l) => l.id === lang.language);
      const proficiencyOption = proficiencyLevels.find((p) => p.id === lang.proficiency);
      const languageName = languageOption?.englishName || 'Unknown Language';
      const proficiency = proficiencyOption?.level || 'Unknown Proficiency';
      return `
Language ${index + 1}:
- Language: ${languageName}
- Proficiency: ${proficiency}
      `.trim();
    }).join('\n\n');
  };

  // Transform PersonLanguage data to Language format for the component
  const transformedLanguages = useMemo(() => {
    // Wait for all data to be loaded before transforming
    if (isComponentLoading) {
      return [];
    }

    // If no language skills, return empty array
    if (!languageSkills.length) {
      return [];
    }

    // Only transform if we have dictionary data
    if (!availableLanguages.length || !proficiencyLevels.length) {
      return [];
    }

    return languageSkills.map((item) => {
      const languageOption = availableLanguages.find(
        (lang) => lang.id === item.language
      );
      const proficiencyOption = proficiencyLevels.find(
        (prof) => prof.id === item.proficiency
      );

      return {
        id: item.id.toString(),
        languageId: item.language,
        name: languageOption?.englishName || "Unknown Language",
        proficiencyId: item.proficiency,
        proficiency: proficiencyOption?.level || "Unknown Level",
        includeInCV: true, // Default to true since we removed isStarred
      };
    });
  }, [
    languageSkills,
    availableLanguages,
    proficiencyLevels,
    isComponentLoading,
  ]);

  const handleAddLanguage = async () => {
    setShowAddValidation(true);
    if (!newLanguage.languageId || !newLanguage.proficiencyId || !userId)
      return;

    const languageOption = availableLanguages.find(
      (lang) => lang.id === newLanguage.languageId
    );
    const proficiencyOption = proficiencyLevels.find(
      (prof) => prof.id === newLanguage.proficiencyId
    );

    if (!languageOption || !proficiencyOption) return;

    setIsSaving(true);
    try {
      const languageSkillData = {
        id: 0, // Will be set by backend
        userId: userId,
        languageId: newLanguage.languageId,
        proficiencyId: newLanguage.proficiencyId,
        language: languageOption,
        proficiency: proficiencyOption,
      };

      await addLanguageSkill.mutateAsync(languageSkillData);

      notifications.show({
        title: "Success",
        message: "Language added successfully",
        color: "green",
      });
      onMutated?.();

      setNewLanguage({
        languageId: null,
        proficiencyId: null,
      });
      setIsAddFormOpen(false);
    } catch (error) {
      console.error("Error adding language:", error);
      notifications.show({
        title: "Error",
        message: "Failed to add language. Please try again.",
        color: "red",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveLanguage = async (id: string) => {
    if (!userId) return;

    const languageToDelete = transformedLanguages.find(
      (lang) => lang.id === id
    );
    if (!languageToDelete) return;

    setIsSaving(true);
    try {
      const languageSkillData = {
        id: parseInt(id),
        userId: userId,
        languageId: languageToDelete.languageId,
        proficiencyId: languageToDelete.proficiencyId,
        language: availableLanguages.find(
          (l) => l.id === languageToDelete.languageId
        )!,
        proficiency: proficiencyLevels.find(
          (p) => p.id === languageToDelete.proficiencyId
        )!,
      };

      await deleteLanguageSkill.mutateAsync(languageSkillData);

      notifications.show({
        title: "Success",
        message: "Language deleted successfully",
        color: "green",
      });
      onMutated?.();
    } catch (error) {
      console.error("Error deleting language:", error);
      notifications.show({
        title: "Error",
        message: "Failed to delete language. Please try again.",
        color: "red",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleInclude = async (id: string) => {
    // Note: Since we removed isStarred from the backend model,
    // this functionality is temporarily disabled
    // All languages are included by default now
    notifications.show({
      title: "Info",
      message: "All languages are automatically included in CV",
      color: "blue",
    });
  };

  const handleEditLanguage = (language: Language) => {
    setEditingLanguageId(language.id);
    setEditLanguage({
      languageId: language.languageId,
      proficiencyId: language.proficiencyId,
    });
  };

  const saveLanguageEdit = async () => {
    setShowEditValidation(true);
    if (
      !editingLanguageId ||
      !editLanguage.languageId ||
      !editLanguage.proficiencyId ||
      !userId
    )
      return;

    const languageOption = availableLanguages.find(
      (lang) => lang.id === editLanguage.languageId
    );
    const proficiencyOption = proficiencyLevels.find(
      (prof) => prof.id === editLanguage.proficiencyId
    );

    if (!languageOption || !proficiencyOption) return;

    setIsSaving(true);
    try {
      const languageSkillData = {
        id: parseInt(editingLanguageId),
        userId: userId,
        languageId: editLanguage.languageId,
        proficiencyId: editLanguage.proficiencyId,
        language: languageOption,
        proficiency: proficiencyOption,
      };

      await updateLanguageSkill.mutateAsync(languageSkillData);

      notifications.show({
        title: "Success",
        message: "Language updated successfully",
        color: "green",
      });
      onMutated?.();

      setEditingLanguageId(null);
      setEditLanguage({
        languageId: null,
        proficiencyId: null,
      });
    } catch (error) {
      console.error("Error updating language:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update language. Please try again.",
        color: "red",
      });
    } finally {
      setIsSaving(false);
    }

    setEditingLanguageId(null);
  };

  const openDeleteConfirmation = (language: Language) => {
    setLanguageToDelete(language);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (languageToDelete) {
      handleRemoveLanguage(languageToDelete.id);
      setDeleteModalOpen(false);
    }
  };

  // Prepare select data
  const languageSelectData = availableLanguages.map((lang) => ({
    value: lang.id.toString(),
    label: lang.englishName,
  }));

  const proficiencySelectData = proficiencyLevels.map((prof) => ({
    value: prof.id.toString(),
    label: prof.level,
  }));

  if (isComponentLoading) {
    return (
      <CVSectionContainer sectionId="languages" title="Languages">
        <Stack align="center" gap="md" py="xl">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Loading languages data...
          </Text>
        </Stack>
      </CVSectionContainer>
    );
  }

  return (
    <CVSectionContainer
      sectionId="languages"
      title="Languages"
      buttonText={isEditMode ? "Add Language" : undefined}
      onAddClick={isEditMode ? () => setIsAddFormOpen(true) : undefined}
      instructions={isEditMode ? INSTRUCTIONS : undefined}
      personId={userId}
      cvId={cvId}
      getSectionData={getLanguagesSectionData}
    >
      <ValidationResult section={ValidateableCvSections.Languages} />

      <Collapse in={isAddFormOpen}>
        <Box
          mb="md"
          p="md"
          style={{
            border: `2px solid ${theme.colors.cyan[7]}`,
            borderRadius: theme.radius.sm,
            backgroundColor: isDark ? theme.colors.dark[6] : FormUIColor.ADD_FORM_BG,
          }}
        >
          <Stack gap="md">
            <Select
              label="Language"
              placeholder="Select a language"
              data={languageSelectData}
              value={newLanguage.languageId?.toString() || null}
              onChange={(value) =>
                setNewLanguage({
                  ...newLanguage,
                  languageId: value ? parseInt(value) : null,
                })
              }
              required
              searchable
            />

            <Select
              label="Proficiency Level"
              placeholder="Select your proficiency level"
              data={proficiencySelectData}
              value={newLanguage.proficiencyId?.toString() || null}
              onChange={(value) =>
                setNewLanguage({
                  ...newLanguage,
                  proficiencyId: value ? parseInt(value) : null,
                })
              }
              required
            />

            <Group justify="flex-end" gap="sm">
              {showAddValidation && (() => {
                const errors: string[] = [];
                if (!newLanguage.languageId) errors.push('Language is required');
                if (!newLanguage.proficiencyId) errors.push('Proficiency level is required');
                return errors.length > 0 ? (
                  <Alert color="red" w="100%" py="xs">
                    <List size="sm" spacing={2}>
                      {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                    </List>
                  </Alert>
                ) : null;
              })()}
              <Button
                variant="outline"
                leftSection={<XMarkIcon width={16} height={16} />}
                onClick={() => { setIsAddFormOpen(false); setShowAddValidation(false); }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                leftSection={<CheckIcon width={16} height={16} />}
                onClick={handleAddLanguage}
                loading={isSaving}
              >
                Add Language
              </Button>
            </Group>
          </Stack>
        </Box>
      </Collapse>

      {transformedLanguages.length === 0 && !isAddFormOpen && (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No languages added yet. Click "Add Language" to add your language
          proficiencies.
        </Text>
      )}

      <Stack gap="sm">
        {transformedLanguages.map((language) => (
          <React.Fragment key={language.id}>
            {editingLanguageId === language.id ? (
              // Show edit form instead of the language item when editing
              <Box
                p="md"
                style={{
                  border: `2px solid ${theme.colors.blue[5]}`,
                  borderRadius: theme.radius.sm,
                  backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.blue[0],
                }}
              >
                <Text size="sm" c="blue" fw={500} mb="md">
                  Editing: {language.name}
                </Text>
                <Stack gap="md">
                  <Select
                    label="Language"
                    placeholder="Select a language"
                    data={languageSelectData}
                    value={editLanguage.languageId?.toString() || null}
                    onChange={(value) =>
                      setEditLanguage({
                        ...editLanguage,
                        languageId: value ? parseInt(value) : null,
                      })
                    }
                    required
                    searchable
                  />

                  <Select
                    label="Proficiency Level"
                    placeholder="Select your proficiency level"
                    data={proficiencySelectData}
                    value={editLanguage.proficiencyId?.toString() || null}
                    onChange={(value) =>
                      setEditLanguage({
                        ...editLanguage,
                        proficiencyId: value ? parseInt(value) : null,
                      })
                    }
                    required
                  />

                  <Group justify="flex-end" gap="sm">
                    {showEditValidation && (() => {
                      const errors: string[] = [];
                      if (!editLanguage.languageId) errors.push('Language is required');
                      if (!editLanguage.proficiencyId) errors.push('Proficiency level is required');
                      return errors.length > 0 ? (
                        <Alert color="red" w="100%" py="xs">
                          <List size="sm" spacing={2}>
                            {errors.map((e) => <List.Item key={e}>{e}</List.Item>)}
                          </List>
                        </Alert>
                      ) : null;
                    })()}
                    <Button
                      variant="outline"
                      leftSection={<XMarkIcon width={16} height={16} />}
                      onClick={() => { setEditingLanguageId(null); setShowEditValidation(false); }}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      leftSection={<CheckIcon width={16} height={16} />}
                      onClick={saveLanguageEdit}
                      loading={isSaving}
                    >
                      Save
                    </Button>
                  </Group>
                </Stack>
              </Box>
            ) : (
              // Show the normal language item when not editing
              <Box
                py="sm"
                style={{
                  borderBottom:
                    transformedLanguages.indexOf(language) !==
                      transformedLanguages.length - 1
                      ? `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[2]}`
                      : "none",
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text fw={500}>{language.name}</Text>
                    <Text size="sm" c="dimmed">
                      {language.proficiency}
                    </Text>
                  </div>
                  {isEditMode && (
                    <Group gap="xs">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => handleEditLanguage(language)}
                        disabled={isSaving}
                      >
                        <PencilIcon style={{ width: 16, height: 16 }} />
                      </ActionIcon>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => openDeleteConfirmation(language)}
                        disabled={isSaving}
                      >
                        <TrashIcon style={{ width: 16, height: 16 }} />
                      </ActionIcon>
                    </Group>
                  )}
                </Group>
              </Box>
            )}
          </React.Fragment>
        ))}
      </Stack>

      <DeleteConfirmationModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemName={languageToDelete?.name}
        isLoading={isSaving}
      />
    </CVSectionContainer>
  );
}
